import { Helmet } from "react-helmet-async";
import { MessageCircle, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";

const Index = () => {
  const whatsappNumber = "5537998673719";
  const whatsappMessage = encodeURIComponent("Olá! Gostaria de saber mais sobre calcário agrícola.");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-calcarear-beige/20 to-white">
      <Helmet>
        <title>Calcarear - Calcário Agrícola de Qualidade | Correção de Solo para Agricultura</title>
        <meta name="description" content="Fornecedor de calcário agrícola calcítico e dolomítico para correção de solo. Mineração própria com produtos de alta qualidade para aumentar a produtividade da sua lavoura. Entre em contato via WhatsApp." />
        <meta name="keywords" content="calcário agrícola, calcário calcítico, calcário dolomítico, correção de solo, agricultura, agronegócio, mineração, fertilizante, solo agrícola, produtividade agrícola, lavoura, fazenda, plantação" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://calcarear.com.br/" />
        <meta property="og:title" content="Calcarear - Calcário Agrícola de Qualidade" />
        <meta property="og:description" content="Fornecedor de calcário agrícola calcítico e dolomítico para correção de solo. Mineração própria com produtos de alta qualidade." />
        <meta property="og:image" content="https://calcarear.com.br/lovable-uploads/b6fce5bd-2dc9-4cae-8f81-ebb97776582b.png" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://calcarear.com.br/" />
        <meta property="twitter:title" content="Calcarear - Calcário Agrícola de Qualidade" />
        <meta property="twitter:description" content="Fornecedor de calcário agrícola calcítico e dolomítico para correção de solo. Mineração própria com produtos de alta qualidade." />
        <meta property="twitter:image" content="https://calcarear.com.br/lovable-uploads/b6fce5bd-2dc9-4cae-8f81-ebb97776582b.png" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="Portuguese" />
        <meta name="author" content="Calcarear" />
        <meta name="geo.region" content="BR" />
        <meta name="geo.placename" content="Brasil" />
        
        <link rel="canonical" href="https://calcarear.com.br/" />
        
        {/* Structured Data - Local Business */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Calcarear",
            "description": "Fornecedor de calcário agrícola calcítico e dolomítico para correção de solo",
            "url": "https://calcarear.com.br",
            "telephone": whatsappNumber,
            "image": "https://calcarear.com.br/lovable-uploads/b6fce5bd-2dc9-4cae-8f81-ebb97776582b.png",
            "priceRange": "$$",
            "@id": "https://calcarear.com.br",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "BR"
            }
          })}
        </script>
        
        {/* Structured Data - Product */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Calcário Agrícola",
            "description": "Calcário agrícola calcítico e dolomítico para correção de solo e aumento da produtividade agrícola",
            "brand": {
              "@type": "Brand",
              "name": "Calcarear"
            },
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock",
              "priceValidUntil": "2025-12-31",
              "priceCurrency": "BRL"
            }
          })}
        </script>
      </Helmet>

      <main className="flex-1 flex items-center justify-center px-4 py-8 sm:py-12 md:py-16">
        <div className="max-w-4xl w-full text-center">
          {/* Logo */}
          <div className="mb-8 sm:mb-12 flex justify-center">
            <img
              src="/lovable-uploads/b6fce5bd-2dc9-4cae-8f81-ebb97776582b.png"
              alt="Calcarear - Calcário Agrícola"
              className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 object-contain"
              width="192"
              height="192"
              loading="eager"
              fetchPriority="high"
            />
          </div>

          {/* Main Content */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-calcarear-dark-green mb-4 sm:mb-6 leading-tight px-2">
            Calcário Agrícola de Alta Qualidade
          </h1>
          
          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 mb-8 sm:mb-10 max-w-2xl mx-auto px-4">
            Venda direta da mineração para o produtor rural, sem intermediários. Calcário calcítico e dolomítico para correção de solo e aumento da produtividade agrícola.
          </p>

          {/* WhatsApp CTA - Destaque Principal */}
          <div className="space-y-4 mb-12 sm:mb-16">
            <Button
              asChild
              size="lg"
              className="bg-[#25D366] hover:bg-[#20BA5A] text-white text-lg sm:text-xl py-7 sm:py-8 px-10 sm:px-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto"
            >
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="mr-3 h-6 w-6 sm:h-7 sm:w-7" />
                Solicite seu Orçamento pelo WhatsApp
              </a>
            </Button>
            <p className="text-sm sm:text-base text-gray-600">
              Atendimento rápido e personalizado
            </p>
          </div>

          {/* Products Highlight */}
          <section className="mb-10 sm:mb-12 px-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-calcarear-dark-green mb-6 sm:mb-8">
              Nossos Produtos
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-lg sm:text-xl font-semibold text-calcarear-green mb-2">
                  Calcário Calcítico
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Alto teor de cálcio (CaO), ideal para correção de solos ácidos
                </p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                <h3 className="text-lg sm:text-xl font-semibold text-calcarear-green mb-2">
                  Calcário Dolomítico
                </h3>
                <p className="text-gray-600 text-sm sm:text-base">
                  Rico em magnésio (MgO) e cálcio, ideal para solos deficientes
                </p>
              </div>
            </div>
          </section>

          {/* Benefits */}
          <section className="bg-white rounded-lg p-6 sm:p-8 shadow-md mb-10 sm:mb-12 mx-auto max-w-3xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-calcarear-dark-green mb-6">
              Por Que Escolher a Calcarear?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 text-left">
              <div className="flex items-start space-x-3">
                <div className="bg-calcarear-green/20 p-2 rounded-full flex-shrink-0">
                  <svg className="h-5 w-5 text-calcarear-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-calcarear-dark-green mb-1">Direto da Mineração</h3>
                  <p className="text-gray-600 text-sm">Venda direta para o produtor rural, sem intermediários</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-calcarear-green/20 p-2 rounded-full flex-shrink-0">
                  <svg className="h-5 w-5 text-calcarear-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-calcarear-dark-green mb-1">Alta Qualidade</h3>
                  <p className="text-gray-600 text-sm">Produtos certificados e testados para máxima eficiência</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-calcarear-green/20 p-2 rounded-full flex-shrink-0">
                  <svg className="h-5 w-5 text-calcarear-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-calcarear-dark-green mb-1">Entrega Garantida</h3>
                  <p className="text-gray-600 text-sm">Logística eficiente para todo o Brasil</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-calcarear-green/20 p-2 rounded-full flex-shrink-0">
                  <svg className="h-5 w-5 text-calcarear-green" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-calcarear-dark-green mb-1">Atendimento Personalizado</h3>
                  <p className="text-gray-600 text-sm">Suporte técnico e consultoria especializada</p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Info */}
          <section className="text-center px-4">
            <h2 className="text-xl sm:text-2xl font-semibold text-calcarear-dark-green mb-4 sm:mb-6">
              Entre em Contato
            </h2>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-gray-700">
              <a 
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-calcarear-green transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span className="text-sm sm:text-base">WhatsApp</span>
              </a>
              <a 
                href="mailto:contato@calcarear.com.br"
                className="flex items-center gap-2 hover:text-calcarear-green transition-colors"
              >
                <Mail className="h-5 w-5" />
                <span className="text-sm sm:text-base">contato@calcarear.com.br</span>
              </a>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-calcarear-dark-green text-white py-6 sm:py-8 px-4 mt-auto">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm sm:text-base mb-2">
            © {new Date().getFullYear()} Calcarear - Todos os direitos reservados
          </p>
          <p className="text-xs sm:text-sm text-white/80">
            Calcário Agrícola de Qualidade para o Agronegócio Brasileiro
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
